import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../config/axios';
import ertinuLogo from '../../assets/images/ertinu_logo.png';
import aproLogo from '../../assets/images/apro_logo.png';
import forgotPasswordImage from '../../assets/images/forgot-password.png';

const LoadingDots = () => {
  return (
    <div className="flex space-x-1">
      <div className="w-1.5 h-1.5 bg-white rounded-full animate-[loading_0.9s_ease-in-out_infinite]" />
      <div className="w-1.5 h-1.5 bg-white rounded-full animate-[loading_0.9s_ease-in-out_0.3s_infinite]" />
      <div className="w-1.5 h-1.5 bg-white rounded-full animate-[loading_0.9s_ease-in-out_0.6s_infinite]" />
    </div>
  );
};

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // const navigate = useNavigate(); // Not used in this component

  // Domain kontrolü ile logo seçimi
  const getLogoByDomain = () => {
    const hostname = window.location.hostname;
    if (hostname.includes('ertinu.com')) {
      return ertinuLogo;
    } else {
      return aproLogo; // localhost, apro.com.tr ve diğer tüm durumlar için
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Email formatını kontrol et
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Please enter a valid email address.');
        setLoading(false);
        return;
      }

      await api.post('/auth/forgot-password', { 
        email 
      });

      // Yapay gecikme (0.5 saniye)
      await new Promise(resolve => setTimeout(resolve, 500));

      setSuccess('If this email address is registered in our system, a password reset link has been sent.');
      setEmail(''); // Email'i temizle
      
    } catch (err) {
      console.error('Forgot password error:', err);
      
      if (err.response?.status === 404) {
        // Güvenlik açısından, email bulunamadığında da aynı mesajı göster
        setSuccess('If this email address is registered in our system, a password reset link has been sent.');
        setEmail('');
      } else {
        setError(err.response?.data?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row h-screen">
      {/* Sol Panel - Forgot Password Formu */}
      <div className="w-full lg:w-[500px] bg-[#272c39] h-full flex flex-col px-6 sm:px-8 lg:px-12">
        <div className="mt-8 sm:mt-12 lg:mt-20 mb-8 sm:mb-12 lg:mb-16 flex justify-start w-full">
          <img src={getLogoByDomain()} alt="Company Logo" className="h-10 sm:h-12" />
        </div>

        <div className="w-full">
          <h2 className="text-xl sm:text-2xl font-medium text-white mb-2">Password Reset</h2>
          <p className="text-white/60 text-sm mb-6 sm:mb-8">
            Enter your email address and we'll send you a password reset link.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 sm:py-2 bg-white/10 border border-white/10 text-white rounded-md 
                  focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent
                  placeholder-white/30 text-base"
                placeholder="example@email.com"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !email.trim()}
              className={`w-full py-2.5 sm:py-2 rounded-md transition-all font-medium flex items-center justify-center
                disabled:cursor-not-allowed text-base ${
                loading || !email.trim()
                  ? 'bg-white/20 text-white/50' 
                  : 'bg-white text-[#272c39] hover:bg-white/90'
              }`}
            >
              {loading ? (
                <LoadingDots />
              ) : (
                'Reset Password'
              )}
            </button>

            <div className="flex justify-between">
              <Link 
                to="/login"
                className="text-sm text-white/50 hover:text-white/90 transition-colors"
              >
                Customer Login
              </Link>
              <Link 
                to="/admin"
                className="text-sm text-white/50 hover:text-white/90 transition-colors"
              >
                Agent Login
              </Link>
            </div>

            {error && (
              <div className="bg-red-500/10 text-red-400 text-sm p-3 rounded-md mt-4">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-500/10 text-green-400 text-sm p-3 rounded-md mt-4">
                {success}
              </div>
            )}
          </form>
        </div>

        <div className="mt-auto mb-6 sm:mb-8 text-center w-full">
          <p className="text-white/40 text-xs sm:text-sm">
            {window.location.hostname.includes('ertinu.com') 
              ? '© 2024 ERTINU. All rights reserved.'
              : '© 2024 APRO. All rights reserved.'
            }
          </p>
        </div>
      </div>

      {/* Sağ Panel - Görsel ve Açıklama */}
      <div className="hidden lg:flex flex-1 bg-[#f5f7f9] flex-col items-center justify-center">
        <div className="max-w-[600px] text-center">
          <img 
            src={forgotPasswordImage} 
            alt="Forgot Password" 
            className="mx-auto max-h-[300px] lg:max-h-[400px] object-contain mb-6 lg:mb-8"
          />
          <div className="text-center px-6 lg:px-8">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2">
              Forgot your password?
            </h2>
            <p className="text-base lg:text-lg text-gray-600">
              Don't worry! Enter your email address and we'll send you a password reset link so you can safely access your account again.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
